import { Dayjs } from 'dayjs';
import ArrayResponse from '../ArrayResponse';
import { toAPITDate } from '../helpers';
import PagedResponse from '../PagedResponse';
import { buildQueryParams, QueryParameters } from '../QueryParameters';
import { Delete, Get, Post } from '../requestBuilder/buildFetchRequest';
import NotifyOverdueTimesheetsRequest, { OverdueTimesheet } from './NotifyOverdueTimesheetsRequest';
import OverdueTimesheetsRequest from './OverdueTimesheetsRequest';
import OverdueTimesheetsResponse from './OverdueTimesheetsResponse';
import TimeSheet from './TimeSheet';
import TimesheetApprover from './TimesheetApprover';
import TimesheetApproverDetails from './TimesheetApproverDetails';
import TimeSheetQueryOptions from './TimeSheetQueryOptions';

export const getTimeSheets = (query: QueryParameters<TimeSheet, TimeSheetQueryOptions>): Promise<PagedResponse<TimeSheet>> => (
  Get('/api/timeSheets')
    .AddQueryParams(buildQueryParams(query))
    .Execute()
);

export const getApprovableTimeSheets = (approverId: number, query: QueryParameters<TimeSheet, TimeSheetQueryOptions>): Promise<PagedResponse<TimeSheet>> => (
  Get(`/api/timeSheets/approvable/${approverId}`)
    .AddQueryParams(buildQueryParams(query))
    .Execute()
);

export const getMyTimeSheets = (query: QueryParameters<TimeSheet, Omit<TimeSheetQueryOptions, 'employeeId'>>): Promise<PagedResponse<TimeSheet>> => (
  Get('/api/my/timeSheets')
    .AddQueryParams(buildQueryParams(query))
    .Execute()
);

export const getTimeSheet = (id: number): Promise<TimeSheet> => (
  Get(`/api/timeSheets/${id}`)
    .Execute()
);

export const getOrCreateTimeSheet = (date: Dayjs): Promise<TimeSheet> => (
  Get(`/api/my/timeSheets/${toAPITDate(date)}`)
    .Execute()
);

export const deleteTimeSheet = (id: number): Promise<void> => (
  Delete(`/api/timeSheets/${id}`)
    .Execute()
);

export const submitTimesheet = (id: number) : Promise<void> => (
  Post(`/api/my/timeSheets/${id}/submit`)
    .Execute()
);

export const unsubmitTimesheet = (id: number) : Promise<void> => (
  Post(`/api/my/timeSheets/${id}/unsubmit`)
    .Execute()
);

export const rejectTimesheet = (id: number, message: string) : Promise<void> => (
  Post(`/api/timeSheets/${id}/reject`)
    .AddBody({ comment: message })
    .Execute()
);

export const unrejectTimesheet = (id: number, message: string) : Promise<void> => (
  Post(`/api/timeSheets/${id}/unreject`)
    .AddBody({ comment: message })
    .Execute()
);

export const approveTimesheet = (id: number, message: string) : Promise<void> => (
  Post(`/api/timeSheets/${id}/approve`)
    .AddBody({ comment: message })
    .Execute()
);

export const approveMissingTimesheet = (dateToUse: Dayjs, employeeId: number, message: string) : Promise<number> => (
  Post(`/api/timeSheets/${dateToUse}/approve/missing`)
    .AddBody({ employeeId, comment: message })
    .Execute()
);

export const unapproveTimesheet = (id: number, message: string) : Promise<void> => (
  Post(`/api/timeSheets/${id}/unapprove`)
    .AddBody({ comment: message })
    .Execute()
);

export const getTimesheetApprovers = (id: number): Promise<ArrayResponse<TimesheetApprover>> => (
  Get(`/api/timeSheets/${id}/approvers`)
    .Execute()
);

export const addEmployeeApprover = (employeeId: number, approverId: number): Promise<void> => (
  Post(`/api/timeSheets/approver/${approverId}/employee/${employeeId}`)
    .Execute()
);

export const addDepartmentApprover = (departmentName: string, approverId: number): Promise<void> => (
  Post(`/api/timeSheets/approver/${approverId}/department/${departmentName}`)
    .Execute()
);

export const addSubDepartmentApprover = (subdepartmentId: number, approverId: number): Promise<void> => (
  Post(`/api/timeSheets/approver/${approverId}/subdepartment/${subdepartmentId}`)
    .Execute()
);

export const deleteEmployeeApprover = (employeeId: number, approverId: number): Promise<void> => (
  Delete(`/api/timeSheets/approver/${approverId}/employee/${employeeId}`)
    .Execute()
);

export const deleteDepartmentApprover = (departmentName: string, approverId: number): Promise<void> => (
  Delete(`/api/timeSheets/approver/${approverId}/department/${departmentName}`)
    .Execute()
);

export const deleteSubDepartmentApprover = (subdepartmentId: number, approverId: number): Promise<void> => (
  Delete(`/api/timeSheets/approver/${approverId}/subdepartment/${subdepartmentId}`)
    .Execute()
);

export const getIsEmployeeATimesheetApprover = (id: number) : Promise<boolean> => (
  Get(`/api/timeSheets/approver/status/${id}`)
    .Execute()
);

export const getApproverDetails = (id: number) : Promise<TimesheetApproverDetails> => (
  Get(`/api/timeSheets/approver/${id}/details`)
    .Execute()
);

export const getMyApproverDetails = () : Promise<{
  employees: number[];
}> => (
  Get('/api/timeSheets/my/approver/details')
    .Execute()
);

export const getOverdueTimesheets = (query: OverdueTimesheetsRequest) : Promise<OverdueTimesheetsResponse[]> => (
  Get('/api/timeSheets/overdue')
    .AddQueryParams({ ...query })
    .Execute()
);

export const getMyOverdueTimesheets = (query: OverdueTimesheetsRequest) : Promise<OverdueTimesheetsResponse[]> => (
  Get('/api/my/timeSheets/overdue')
    .AddQueryParams({ ...query })
    .Execute()
);

export const createMissingTimesheet = (employeeId: number, dateToUse: Dayjs) : Promise<number> => (
  Post('/api/timeSheets/create')
    .AddBody({ employeeId, dateToUse })
    .Execute()
);

export const notifyOverdueTimesheet = (request: NotifyOverdueTimesheetsRequest, batch: OverdueTimesheet[]) : Promise<void> => (
  Post('/api/timeSheets/overdue/notify')
    .AddBody({
      ...request,
      batch,
    })
    .Execute()
);

export const timesheetGroupableColumns: (keyof TimeSheet)[] = [
  'employeeFirstName',
  'employeeId',
  'employeeLastName',
  'endDate',
  'startDate',
  'totalHours',
  'totalTimecards',
  'status',
  'statusDate',
];

export const timesheetFilterableColumns: (keyof TimeSheetQueryOptions)[] = [
  'employeeFirstName',
  'employeeId',
  'employeeLastName',
  'endDate',
  'startDate',
  'totalHours',
  'totalTimecards',
  'status',
  'statusDate',
];

export const timesheetSortableColumns: (keyof TimeSheet)[] = [
  ...timesheetGroupableColumns,
  'id',
];
